import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { PitchCardModalBase } from '../pitchcard-modal-base';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ElementRef,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { Carousel, CarouselModule } from 'primeng/carousel';
import { PitchCardFolderThumbnailComponent } from '../../../pitchcard-folder-thumbnail/pitchcard-folder-thumbnail.component';
import { PocketSetupComponent } from '../../../pocket-setup/pocket-setup.component';
import { MyPocket } from '@App/common/interfaces';
import { signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MyPocketsService } from '../../../../../choosen-history/services';
import {
  BusinessService,
  UserCommonApiService,
  VCardService
} from '@App/common/services';
import { NotificationService } from '@Shared/services';
import { PitchCardModalsWrapperService } from '@App/modules/pitch-card-shared/services';
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import { notifyModalError, notifyModalSuccess } from '../modal-feedback';

@Component({
  selector: 'app-pitchcard-save-pocket-modal',
  standalone: true,
  providers: [MyPocketsService],
  imports: [
    CommonModule,
    DialogModule,
    CarouselModule,
    PitchCardFolderThumbnailComponent,
    PocketSetupComponent
  ],
  templateUrl: './pitchcard-save-pocket-modal.component.html'
})
export class PitchCardSavePocketModalComponent
  extends PitchCardModalBase
  implements OnChanges
{
  @Input() showSaveMobileBusinessDialog: any;
  @Input() showSaveBusinessDialog: any;
  @Input() detailsPocketModal: any;
  @Input() isMobile: any;
  @Input() isSearch: any;
  @Input() mobilePocketScale: any;
  @Input() currentPitchCard: any;
  @Output() saveOpened = new EventEmitter<void>();
  @Output() requestContactSwap = new EventEmitter<void>();

  carouselAllPockets = signal<MyPocket[]>([]);
  carouselAllPocketsLoader = false;
  numScroll = 1;
  numVisible = 4;
  responsiveOptions = [
    { breakpoint: '1920px', numVisible: 4, numScroll: 1 },
    { breakpoint: '1069px', numVisible: 3, numScroll: 3 },
    { breakpoint: '820px', numVisible: 2, numScroll: 2 },
    { breakpoint: '620px', numVisible: 1, numScroll: 1 }
  ];
  modalErrors = { newPocketName: null, newPocketColor: null };
  @ViewChild('pocketsCarousel', { static: false }) pocketsCarousel: Carousel;
  private myPocketsService = inject(MyPocketsService);
  private businessService = inject(BusinessService);
  private vCardService = inject(VCardService);
  private userCommonService = inject(UserCommonApiService);
  private notificationService = inject(NotificationService);
  private pitchCardModalsWrapperService = inject(PitchCardModalsWrapperService);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['showSaveBusinessDialog']?.currentValue === true) {
      this.saveBusinessDialogVariablesInit();
    }
  }

  downloadContact() {
    if (!!this.vCardService.vCard) {
      // saveAs() must stay synchronous and first: Safari only shows the
      // native "Add to Contacts" prompt for vCards when the download is
      // triggered directly inside the user gesture. Deferring it into an
      // async subscribe() callback makes Safari treat it as a plain file
      // download instead.
      saveAs(
        this.vCardService.downloadUrl,
        this.vCardService.vCard?.name?.firstNames
          ? this.vCardService.vCard.name.firstNames
          : 'vCard'
      );
      this.vCardService.downloadUrl = '';
      const isEmbeded = this.activatedRoute.snapshot.url.find(
        ({ path }) => path === 'embedded-card'
      );

      // Use the keepalive-based beacon rather than the HttpClient/XHR call:
      // Safari aborts pending XHR/fetch requests when saveAs() above kicks
      // off its native download/"Add to Contacts" handling, which cancelled
      // this analytics call before it could complete.
      this.businessService.addToContactAnalyticsBeacon(
        this.vCardService.businessDetails.id
      );

      !isEmbeded && this.requestContactSwap.emit();
    }
    this.onFlag('showSaveMobileBusinessDialog', false);
  }

  openSaveBusinessDialog() {
    this.onFlag('showSaveMobileBusinessDialog', false);
    this.onFlag('showSaveBusinessDialog', true);
    this.saveOpened.emit();
  }

  clearCarouselAllPocketsVariable() {
    this.carouselAllPockets.set([]);
    this.userCommonService.pendingMethod = null;
  }

  clearDetailsModalOptions() {
    this.modalErrors = {
      newPocketName: null,
      newPocketColor: null
    };
  }

  getVisibleSlideNumbers() {
    if (!isPlatformBrowser(this.platformId)) {
      return 4; // Default value for SSR
    }

    if (window.innerWidth >= 1069) {
      return 4;
    } else if (window.innerWidth >= 820 && window.innerWidth < 1069) {
      return 3;
    } else if (window.innerWidth >= 620 && window.innerWidth < 820) {
      return 2;
    } else {
      return 1;
    }
  }

  newPocketSubmit(params: any) {
    this.myPocketsService.createNewPocket(params).subscribe({
      next: (result) => {
        const message = 'The pocket was created successfully';

        this.finishPocketProcess(result, message);
      },
      error: (error) => {
        const message = 'Error while Pocket creating';
      }
    });
  }

  saveBusinessToPocket(pocketId: number, newPocketName: string) {
    const businessId = this.currentPitchCard.id;

    this.myPocketsService
      .addBusinessToPocket(businessId, pocketId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result.data) {
            this.pitchCardModalsWrapperService.disableSaveBusinessDialog();
            notifyModalSuccess(
              this.notificationService,
              newPocketName
                ? 'The business was saved successfully inside ' + newPocketName
                : 'The business was saved successfully'
            );
          } else {
            this.notificationService.showNotification(
              'SUCCESS',
              result?.message ? result.message : ''
            );
          }
        },
        error: (error) => {
          this.pitchCardModalsWrapperService.disableSaveBusinessDialog();
          notifyModalError(this.notificationService, error);
        }
      });
  }

  finishPocketProcess(result: any, message: string) {
    if (result.data) {
      this.onFlag('detailsPocketModal', false);
      this.notificationService.showNotification('SUCCESS', message);
      this.getCarouselAllPockets();
      this.saveBusinessToPocket(
        result.data.id,
        result?.data?.name ? result.data.name : 'New Pocket'
      );
    }
  }

  saveBusinessDialogVariablesInit() {
    this.resetCarouselPage();
    this.getCarouselAllPockets();
  }

  resetCarouselPage() {
    if (this.pocketsCarousel) {
      this.pocketsCarousel.page = 0;
    }
  }

  getCarouselAllPockets() {
    this.loaderToggler('carouselAllPocketsLoader');
    this.myPocketsService
      .getAllPockets(null, true, true, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result.data) {
            this.loaderToggler('carouselAllPocketsLoader');
            this.updateCarouselAllPocketVariable(result);
          }
        },
        error: (error) => {
          this.loaderToggler('carouselAllPocketsLoader');
          this.pitchCardModalsWrapperService.disableSaveBusinessDialog();
          notifyModalError(this.notificationService, error);
        }
      });
  }

  updateCarouselAllPocketVariable(requestResult: any) {
    const allPockets = requestResult.data;
    allPockets.forEach((pocket: any) => {
      pocket.color =
        pocket?.hexColor || this.myPocketsService.defaultColors[pocket.color];
    });
    this.carouselAllPockets.set(allPockets);
  }

  loaderToggler(loaderName: string) {
    if (this[loaderName]) {
      this[loaderName] = false;
      return;
    }
    this[loaderName] = true;
  }
}
